import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, Plus, Eye, Download, Trash2 } from 'lucide-react';
import api from '../services/api';

const STATUS = {
  draft:                { label: 'Draft',              bg: '#f1f5f9', color: '#475569' },
  sampling:             { label: 'Sampling…',          bg: '#eff6ff', color: '#1e40af' },
  draft_codelist_ready: { label: 'Codelist Ready',     bg: '#fffbeb', color: '#92400e' },
  codelist_merge_pending: { label: 'Choose Merge',     bg: '#fff7ed', color: '#c2410c' },
  processing:           { label: 'Processing',         bg: '#eef2ff', color: '#3730a3' },
  completed:            { label: 'Completed',          bg: '#f0fdf4', color: '#065f46' },
  failed:               { label: 'Failed',             bg: '#fff1f2', color: '#9f1239' },
};

const inp = {
  width: '100%', padding: '7px 12px 7px 36px', border: '1.5px solid #e2e8f0',
  borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#334155',
};
const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0f172a', color: '#fff',
  border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
};
const btnIcon = (color = '#64748b') => ({
  background: 'none', border: 'none', cursor: 'pointer', color, padding: 4,
  borderRadius: 6, display: 'inline-flex', alignItems: 'center',
  transition: 'background 0.15s',
});

function StatusChip({ status }) {
  const s = STATUS[status] || { label: status, bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.color,
    }}>{s.label}</span>
  );
}

function ProgressBar({ value }) {
  return (
    <div style={{ width: '100%' }}>
      <div style={{ background: '#e2e8f0', borderRadius: 4, height: 5, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 4, background: '#3b82f6',
          width: `${value || 0}%`, transition: 'width 0.3s',
        }} />
      </div>
      <span style={{ fontSize: 11, color: '#64748b', marginTop: 3, display: 'block' }}>{value || 0}%</span>
    </div>
  );
}

export default function OECodingList() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const jobsRef = useRef(jobs);

  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const fetchJobs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const response = await api.get('oe_coding/jobs/');
      setJobs(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load coding jobs');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(() => {
      if (jobsRef.current.some(j => j.status === 'processing' || j.status === 'sampling')) {
        fetchJobs({ silent: true });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const currentUserEmail = localStorage.getItem('username') || '';
  const isAdmin = currentUserEmail === 'ram@4sight-global.com';

  const handleDelete = async (jobId) => {
    if (!isAdmin) { setError('Only admin can delete coding jobs.'); return; }
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await api.delete(`oe_coding/jobs/${jobId}/`);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete job');
    }
  };

  const handleDownload = async (job) => {
    try {
      const response = await api.get(`oe_coding/jobs/${job.id}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', job.output_file_name || 'output.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { setError('Failed to download file'); }
  };

  const filteredJobs = jobs.filter(job =>
    job.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (job.description && job.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Header */}
      <div style={{
        background: '#0f172a', borderRadius: 12, padding: '20px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>Open End Coding</div>
          <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>AI-powered open end response coding</div>
        </div>
        <button style={btnPrimary} onClick={() => navigate('/oe-coding/new')}>
          <Plus size={15} /> New Job
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: '#fff1f2', border: '1px solid #fecdd3', borderLeft: '4px solid #f43f5e',
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 13, color: '#9f1239',
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9f1239', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Search + Refresh */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
          <input
            style={inp}
            placeholder="Search jobs…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button style={btnIcon()} onClick={fetchJobs} title="Refresh">
          <RefreshCw size={16} />
        </button>
        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
          {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Job Name', 'Data File', 'Status', 'Progress', 'Creator', 'Created', 'Completed', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px', textAlign: h === 'Actions' ? 'center' : 'left',
                    fontSize: 11.5, fontWeight: 700, color: '#64748b',
                    textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 13 }}>
                    Loading jobs…
                  </td>
                </tr>
              ) : filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 13 }}>
                    {searchTerm ? 'No jobs match your search' : 'No coding jobs yet. Create your first one!'}
                  </td>
                </tr>
              ) : filteredJobs.map(job => (
                <tr key={job.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{job.name}</div>
                    {job.description && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.data_file_name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <StatusChip status={job.status} />
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 130 }}>
                    {(job.status === 'processing' || job.status === 'sampling') ? (
                      <ProgressBar value={job.progress} />
                    ) : job.status === 'completed' ? (
                      <span style={{ fontSize: 12, color: '#64748b' }}>{job.processed_rows}/{job.total_rows} rows</span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                    {job.created_by_name ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                          background: ['#6366f1','#0d9488','#f59e0b','#3b82f6','#8b5cf6'][(job.created_by_name || '').charCodeAt(0) % 5],
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                        }}>{(job.created_by_name || '?')[0].toUpperCase()}</div>
                        <span style={{ fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{job.created_by_name}</span>
                      </div>
                    ) : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(job.created_at)}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(job.completed_at)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button style={btnIcon('#3b82f6')} title="View Details" onClick={() => navigate(`/oe-coding/${job.id}`)}>
                      <Eye size={15} />
                    </button>
                    {job.status === 'completed' && job.output_file && (
                      <button style={btnIcon('#10b981')} title="Download Output" onClick={() => handleDownload(job)}>
                        <Download size={15} />
                      </button>
                    )}
                    <button style={btnIcon('#f43f5e')} title="Delete" onClick={() => handleDelete(job.id)}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
